import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { fetchHomeSlides } from '../api/homepage';

const AUTO_SLIDE_INTERVAL = 3000;

/** Move without animating. The container carries scroll-behavior: smooth in
 *  CSS, so a bare assignment animates the wrap-around and the scroll handler
 *  sees every frame in between. Restores to '' rather than to a captured
 *  previous value so that repeated calls in one frame stay safe. */
function jumpTo(container, left) {
    container.style.scrollBehavior = 'auto';
    container.scrollLeft = left;
    requestAnimationFrame(() => {
        container.style.scrollBehavior = '';
    });
}

function SlideCta({ slide, isClone }) {
    const label = slide.cta_label;
    const link = slide.cta_link;
    if (!label || !link) return null;

    // A clone lives in an aria-hidden subtree: a focusable element inside one
    // is an ARIA violation and a duplicate tab stop.
    const tabIndex = isClone ? -1 : undefined;
    const content = (
        <>
            {label} <i className="fa-solid fa-arrow-right"></i>
        </>
    );

    if (link.startsWith('/')) {
        return (
            <Link to={link} className="slide-btn" tabIndex={tabIndex}>
                {content}
            </Link>
        );
    }
    if (link.startsWith('https://')) {
        return (
            <a
                href={link}
                className="slide-btn"
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={tabIndex}
            >
                {content}
            </a>
        );
    }
    // The model refuses anything else, but the API is a trust boundary: rows
    // also arrive from migrations and restored dumps.
    return null;
}

function Slide({ slide, isClone, priority }) {
    return (
        <div className="slide" aria-hidden={isClone || undefined}>
            <img
                src={slide.image}
                alt={slide.image_alt || ''}
                fetchPriority={priority ? 'high' : undefined}
            />
            <div className="slide-content">
                <h2>{slide.heading}</h2>
                <p>{slide.body}</p>
                <SlideCta slide={slide} isClone={isClone} />
            </div>
        </div>
    );
}

export default function HomeSlider() {
    const { data, isLoading } = useQuery({
        queryKey: ['home-slides'],
        queryFn: fetchHomeSlides,
        retry: 1,
    });
    // The query's default only covers `undefined`. A proxy or CDN that
    // returns 200 with an HTML error page hands back a raw string here --
    // axios swallows the JSON.parse failure -- and the response shape is as
    // much a trust boundary as cta_link's value is.
    const slides = Array.isArray(data) ? data : [];

    const containerRef = useRef(null);
    const autoSlideRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(1);

    const count = slides.length;
    // One slide is a still image: cloning it would flip between three copies
    // of the same photo.
    const loops = count > 1;

    const stopAutoSlide = () => {
        if (autoSlideRef.current) {
            clearInterval(autoSlideRef.current);
            autoSlideRef.current = null;
        }
    };

    // Position on the first real slide. Keyed on the data, not on window.load:
    // by the time the fetch resolves, load has already fired and the old
    // mount-only effect would measure an empty container.
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || !loops) return;
        jumpTo(container, container.clientWidth);
        setCurrentSlide(1);
    }, [loops, count]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !loops) return;

        const handleScroll = () => {
            const slideWidth = container.clientWidth;
            if (!slideWidth) return;
            const maxScroll = container.scrollWidth - slideWidth;

            if (container.scrollLeft <= 1) {
                // On the clone of the last slide; land on the real one.
                jumpTo(container, slideWidth * count);
                setCurrentSlide(count);
            } else if (container.scrollLeft >= maxScroll - 10) {
                jumpTo(container, slideWidth);
                setCurrentSlide(1);
            } else {
                setCurrentSlide(Math.round(container.scrollLeft / slideWidth));
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [loops, count]);

    useEffect(() => {
        const container = containerRef.current;
        const wrapper = container && container.parentElement;
        if (!container || !wrapper || !loops) return;

        const start = () => {
            stopAutoSlide();
            autoSlideRef.current = setInterval(() => {
                // Wrapping is the scroll handler's job: it fires when this
                // lands on a clone.
                container.scrollTo({
                    left: container.scrollLeft + container.clientWidth,
                    behavior: 'smooth',
                });
            }, AUTO_SLIDE_INTERVAL);
        };

        wrapper.addEventListener('mouseenter', stopAutoSlide);
        wrapper.addEventListener('mouseleave', start);
        wrapper.addEventListener('touchstart', stopAutoSlide);
        wrapper.addEventListener('touchend', start);
        start();

        return () => {
            stopAutoSlide();
            wrapper.removeEventListener('mouseenter', stopAutoSlide);
            wrapper.removeEventListener('mouseleave', start);
            wrapper.removeEventListener('touchstart', stopAutoSlide);
            wrapper.removeEventListener('touchend', start);
        };
        // stopAutoSlide only touches a ref, so leaving it out keeps the timer
        // from being torn down and restarted on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loops, count]);

    const goTo = (position) => {
        const container = containerRef.current;
        if (!container) return;
        container.scrollTo({
            left: container.clientWidth * position,
            behavior: 'smooth',
        });
        setCurrentSlide(position);
        stopAutoSlide();
    };

    // Nothing to show and nothing to fall back on -- by design. The invariant
    // that keeps one slide published cannot cover a fresh database, a boot
    // before the seed, or a request that failed, so this path is real.
    if (!isLoading && count === 0) return null;

    const rendered = loops
        ? [
              { slide: slides[count - 1], key: 'clone-last', isClone: true },
              ...slides.map((slide, index) => ({
                  slide,
                  key: slide.pk,
                  isClone: false,
                  priority: index === 0,
              })),
              { slide: slides[0], key: 'clone-first', isClone: true },
          ]
        : slides.map((slide, index) => ({
              slide,
              key: slide.pk,
              isClone: false,
              priority: index === 0,
          }));

    return (
        <div className="slider-wrapper" role="region" aria-label="Image Slider">
            <div className="slider-container" ref={containerRef}>
                {rendered.map(({ slide, key, isClone, priority }) => (
                    <Slide key={key} slide={slide} isClone={isClone} priority={priority} />
                ))}
            </div>
            {loops && (
                <div className="slider-nav">
                    {slides.map((slide, index) => (
                        <button
                            key={slide.pk}
                            type="button"
                            className={
                                index + 1 === currentSlide
                                    ? 'slider-bar active'
                                    : 'slider-bar'
                            }
                            aria-label={`Show slide ${index + 1}: ${slide.heading}`}
                            onClick={() => goTo(index + 1)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
